import { getDistance as haversineDist } from 'ol/sphere';
import { legForEndPoint, pruneRouteGeometry, toDegrees, toRadians } from './helpers';

import { SPERRY_SCHEMA } from './sperrymvm-inline-schema';
import { VoyageRoutePointFeature, VoyageRouteLineFeature, VoyageRoute } from '../models/voyage-routing-model';
import { VoyagePlan } from '../models/vessel-planning-model';
import { ImportResult } from './types/converters.types';

const NMToMeters = 1852; // Nautical miles -> meters
const MPS2KTS = 3600.0 / 1852.0;
const KTS2MPS = 1.0 / MPS2KTS;

export class SperryMVMConverter {
    static check(str: string) {
        try {
            const domParser = new DOMParser();
            var doc = domParser.parseFromString(str, "text/xml");

            const root = doc.firstElementChild;

            if (root && root.nodeName === 'NewDataSet') {
                return doc;
            }
        } catch(e) {
            // don't care
        }
    }

    static import(str: string, options = {}): ImportResult|undefined {
        const opt = {
            assumeGCAbove: 300,
            ...options
        }

        try {
            const route_geometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature> = [];

            const doc = SperryMVMConverter.check(str);
            if (!doc) return;
            const waypoints = doc.getElementsByTagName('ControlPoints');
            let prevCoords: [number, number]|null = null;

            let legGeometryType: string|null = null;
            Array.from(waypoints).forEach(waypoint => {
                const coords: [number, number] = [
                    toDegrees(Number.parseFloat(waypoint.querySelector('Longitude')!.textContent!)),
                    toDegrees(Number.parseFloat(waypoint.querySelector('Latitude')!.textContent!))
                ];

                const wPoint: VoyageRoutePointFeature = {
                    type: 'Feature',
                    geometry: {
                        coordinates: coords,
                        type: 'Point'
                    },
                    properties: {
                        type: 'Hard'
                    }
                };

                const name = waypoint.querySelector('Name');
                if (name)
                {
                    wPoint.properties.name = name.textContent!;
                }

                if (prevCoords) {
                    const dist = haversineDist(prevCoords, coords) / NMToMeters; // Accept minimal error on dist, assuming GC for RL

                    let isGC = true;
                    if (legGeometryType) {
                        isGC = legGeometryType === 'GreatCircleLine';
                    } else if (dist < opt.assumeGCAbove) {
                        isGC = false;
                    }

                    route_geometry.push({
                            type: 'Feature',
                            geometry: {
                                coordinates: [prevCoords, coords],
                                type: 'LineString'
                            },
                            properties: {
                                leg_type: isGC ? 'GreatCircle' : 'Rhumbline'
                            }
                    });
                }

                route_geometry.push(wPoint);

                // The Sperry route has GC/RL marked on the source wp
                legGeometryType = waypoint.querySelector('DepartingControlLineType')!.textContent;

                prevCoords = coords;
            });

            return {
                route: { route_geometry },
                extras: {
                    // Plan related data.

                }
            };
        } catch (error) {
            console.warn(error);
        }
    }

    static export(plan: VoyagePlan, options = {}) {
        const opt = {
            includeWeatherPoints: false,
            routeName: 'Maersk Voyage Planning Route',
            ...options
        }

        // Bail out if no valid route is selected.
        if (plan.selectedRouteIndex < 0 || plan.selectedRouteIndex >= plan.routes.length)
        {
            throw new Error("No valid route selected in plan!");
        }

        // Only include the selected route.
        const route = plan.routes[plan.selectedRouteIndex];

        const xmlString = '<NewDataSet></NewDataSet>';
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, "text/xml"); //important to use "text/xml"
        const newDataSet = xmlDoc.documentElement;

        const prunedRoute = pruneRouteGeometry(route.route_geometry);

        let points = <VoyageRoutePointFeature[]>prunedRoute.filter(item => item.geometry.type === "Point");

        if (!opt.includeWeatherPoints) {
            points = points.filter(item => item.properties.type !== "Weather");
        }

        const legs = <VoyageRouteLineFeature[]>prunedRoute.filter(item => item.geometry.type === "LineString");

        const addNSElement = (tagName: string, text: string) => {
            const el = document.createElementNS('', tagName);
            if (text !== undefined) {
                el.appendChild(document.createTextNode(text));
            }
            return el;
        }

        const summaries = document.createElementNS('', 'Summaries');
        summaries.appendChild(addNSElement('Name', opt.routeName));
        summaries.appendChild(addNSElement('DepartureTime', new Date(plan.etd).toISOString().replace('Z','+00:00')));
        let dist = 0;
        if (route.route_info && route.route_info.totals) {
            if (route.route_info.totals.non_eca && route.route_info.totals.non_eca.distance) {
                dist += route.route_info.totals.non_eca.distance;
            }
            if (route.route_info.totals.eca && route.route_info.totals.eca.distance) {
                dist += route.route_info.totals.eca.distance;
            }
            dist *= 1000; // to meters.
        }
        summaries.appendChild(addNSElement('Distance', "" + dist));
        summaries.appendChild(addNSElement('Description', ''));
        summaries.appendChild(addNSElement('Notes', ''));
        summaries.appendChild(addNSElement('Valid', 'true'));
        summaries.appendChild(addNSElement('LastModified', route.creationTime ? route.creationTime.replace('Z','+00:00') : ''));
        newDataSet.appendChild(summaries);

        let seqNo = 0;
        points.forEach(point => {
            const wp = document.createElementNS('', 'ControlPoints');
            
            wp.appendChild(addNSElement('RouteName', opt.routeName));
            wp.appendChild(addNSElement('SequenceNumber', `${++seqNo}`));
            wp.appendChild(addNSElement('Name', point.properties.name || ''));
            
            wp.appendChild(addNSElement('Latitude', `${toRadians(point.geometry.coordinates[1])}`));
            wp.appendChild(addNSElement('Longitude', `${toRadians(point.geometry.coordinates[0])}`));

            // Needed for the ECDIS to import
            wp.appendChild(addNSElement('TurnRadius', '1852'));
            wp.appendChild(addNSElement('TurnSpeed', '1.0'));
            wp.appendChild(addNSElement('OffTrackAlarmLimitForTurn', '100'));
            wp.appendChild(addNSElement('DepartingTrackSpeed', '1.0'));
            wp.appendChild(addNSElement('OffTrackAlarmLimitForDepartingTrack', '100'));

            const leg = legForEndPoint(legs, point.geometry.coordinates, 0);
            const trackType = leg && leg.properties && leg.properties.leg_type === 'GreatCircle' ? 'GreatCircleLine' : 'RhumbLine';
            wp.appendChild(addNSElement('DepartingControlLineType', trackType));

            let minSpeed = point.properties.speed_minimum || 1.0;
            let maxSpeed = point.properties.speed_maximum || 10.0;
            if (maxSpeed < minSpeed)
            {
                if (point.properties.speed_minimum)
                {
                    maxSpeed = minSpeed;
                }
                else
                {
                    minSpeed = maxSpeed;
                }
            }
            wp.appendChild(addNSElement('MinTurnSpeed', `${minSpeed}`));
            wp.appendChild(addNSElement('MaxTurnSpeed', `${maxSpeed}`));
            wp.appendChild(addNSElement('MinDepartingTrackSpeed', `${minSpeed}`));
            wp.appendChild(addNSElement('MaxDepartingTrackSpeed', `${maxSpeed}`));

            // Needed for the ECDIS to import
            wp.appendChild(addNSElement('AdditionalData', '<AllAdditionalData />'));
            wp.appendChild(addNSElement('LeftOffTrackAlarmLimitForTurn', '100'));
            wp.appendChild(addNSElement('RightOffTrackAlarmLimitForTurn', '100'));
            wp.appendChild(addNSElement('LeftOffTrackAlarmLimitForDepartingTrack', '100'));
            wp.appendChild(addNSElement('RightOffTrackAlarmLimitForDepartingTrack', '100'));
            
            newDataSet.appendChild(wp);
        });

        const xmlSer = new XMLSerializer();
        let result = xmlSer.serializeToString(xmlDoc);

        // inject the inline schema
        result = '<NewDataSet>' + SPERRY_SCHEMA + result.substring(12);

        return result;
    }
}
